import * as React from 'react';

import type {Section as MenuSection} from 'common/components/items-menu/types';
import {Translatable} from 'common/i18n';
import type {SidenavReact} from 'common/components/sidenav/index';

import {ScrollPosition} from './constants';

export interface SidenavAttrs {
  selected?: string;
  loading?: boolean;
  'min-adjacent-width'?: number;
  header?: SidenavHeader;
  'user-id'?: number;
  'storage-key'?: string;
  'hide-collapse-button'?: boolean;
  'fixed-size'?: boolean;
  'is-nested-dashboards-enabled'?: boolean;
}

export interface SidenavState {
  activeDragSectionIdx: number | null;
  collapsedSectionSet: Set<string>;
  expandedItemSet: Set<string>;
  hoveredItemValues: Set<string>;
  isCollapsed: boolean;
  isOverflowMenuOpen: boolean;
  scrollPosition: ScrollPosition;
  selectedItem: SidenavSectionItem | null;
  selectedSectionIndex: number | null;
  scrolledIntoItem: string;
  sections: SidenavSection[];
}

type SidenavCtaHeader = {title?: string; actions: SidenavCta[]};

type SidenavSlotHeader = {title?: string; slot: string; collapsedSlot?: string};

export type SidenavHeader = SidenavCtaHeader | SidenavSlotHeader;

export type SidenavSection<T extends string = string> = {
  /** Key used to uniquely identify this section for persistence. */
  key?: string;
  label?: Translatable;
  items: Array<SidenavSectionItem<T>>;
  dragDropEnabled?: boolean;
  isCollapsible: boolean;
};

export type SidenavSectionItem<T = string> = {
  label: Translatable;
  value: T;
  itemId?: string;
  tooltip?: string;
  userName?: string;
  userId?: number;
  userEmail?: string;

  href?: string;
  target?: string;

  overflowMenuSections?: MenuSection[];
  nestedItems?: SidenavSectionItem[];

  isNew?: boolean;

  [key: string]: any;
};

export enum NestingOrder {
  First = `first`,
  Middle = `middle`,
  Last = `last`,
}

export type SidenavCta = SidenavSectionItem & {
  icon: string;
};

export type PersistedSidenavState = {collapsedSectionSet: string[]; expandedItemSet: string[]};
export type ParsedPersistedSidenavState = Pick<SidenavState, 'collapsedSectionSet' | 'expandedItemSet'>;

export type SidenavItemPosition = {
  parentItem?: SidenavSectionItem;
  sectionIndex?: number;
  itemIndex?: number;
};

export type DragDropEventDetail = {
  item: SidenavSectionItem;
  sectionIndex: number;
  itemIndex: number;

  destinationPosition: SidenavItemPosition;
};

export type SidenavProps = React.ComponentProps<typeof SidenavReact>;
